import React, { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

const ScrollWrap = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  overflow: hidden;
`;

const ScrollTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px calc(50% - 28px);
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FadeLeft = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 28px;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(to right, #ffffff 20%, rgba(255, 255, 255, 0));
`;

const FadeRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 28px;
  height: 100%;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(to left, #ffffff 20%, rgba(255, 255, 255, 0));
`;

const TimePill = styled.button`
  flex: 0 0 auto;
  min-width: 56px;
  border: none;
  background: transparent;
  color: ${({ active }) => (active ? "#c7502b" : "#61746f")};
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  background: ${({ active }) => (active ? "#fff1e8" : "transparent")};
  cursor: pointer;
  padding: 6px 8px;
  transform: none;
  transition:
    color 0.15s ease,
    transform 0.15s ease,
    font-size 0.15s ease;

  &:hover {
    color: #ee754c;
  }
`;

function normalizeTime(time) {
  if (!time) return "";
  return String(time).slice(0, 5);
}

function timeToMinutes(time) {
  const cleanTime = normalizeTime(time);
  const [hours, minutes] = cleanTime.split(":").map(Number);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  return hours * 60 + minutes;
}

function getRestaurantCurrentMinutes(timezone) {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone || "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());

    const hour = Number(parts.find((part) => part.type === "hour")?.value);
    const minute = Number(parts.find((part) => part.type === "minute")?.value);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      throw new Error("Invalid timezone time parts.");
    }

    return hour * 60 + minute;
  } catch (error) {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }
}

function getClosestTimeToNow(times, timezone) {
  if (!times.length) return null;

  const nowMinutes = getRestaurantCurrentMinutes(timezone);

  let bestTime = times[0];
  let bestDistance = Infinity;

  times.forEach((time) => {
    const minutes = timeToMinutes(time);

    if (minutes === null) return;

    const distance = Math.abs(minutes - nowMinutes);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestTime = time;
    }
  });

  return bestTime;
}

export default function ReservationTimeCenterScroll({
  times = [],
  value = null,
  onChange,
  restaurantTimezone = "Europe/London",
}) {
  const trackRef = useRef(null);
  const itemRefs = useRef({});
  const ignoreNextValueCenterRef = useRef(false);
  const didAutoCenterKeyRef = useRef("");
  /* A smooth scroll fires a stream of scroll events. Snap-selecting from them
     handed the selection to whatever time the animation happened to pass on the
     way, which is how clicking one booking ended up selecting another - and why
     it looked like the board was cycling through bookings to find the time.
     Only a scroll the user started may select, and only once it has stopped. */
  const userDrivenScrollRef = useRef(false);
  const settleTimerRef = useRef(null);

  const uniqueTimes = useMemo(() => {
    return Array.from(
      new Set((times || []).map(normalizeTime).filter(Boolean)),
    ).sort((a, b) => {
      return (timeToMinutes(a) || 0) - (timeToMinutes(b) || 0);
    });
  }, [times]);

  const uniqueTimesKey = uniqueTimes.join("|");

  const centerTime = (time, behavior = "smooth") => {
    const track = trackRef.current;
    const item = itemRefs.current[time];

    if (!track || !item) return;

    const itemCenter = item.offsetLeft + item.offsetWidth / 2;
    const targetLeft = itemCenter - track.clientWidth / 2;

    // This scroll is the board's, not the user's, so it must not select.
    userDrivenScrollRef.current = false;

    track.scrollTo({
      left: targetLeft,
      behavior,
    });
  };

  const selectClosestToCenter = () => {
    const track = trackRef.current;

    if (!track || !uniqueTimes.length) return;
    if (!userDrivenScrollRef.current) return;

    const center = track.scrollLeft + track.clientWidth / 2;

    let closestTime = uniqueTimes[0];
    let closestDistance = Infinity;

    uniqueTimes.forEach((time) => {
      const item = itemRefs.current[time];

      if (!item) return;

      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCenter - center);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestTime = time;
      }
    });

    if (closestTime && closestTime !== value) {
      ignoreNextValueCenterRef.current = true;
      onChange?.(closestTime);
    }
  };

  // Wait for the strip to come to rest before selecting, so dragging across six
  // times selects once at the end rather than six times on the way.
  const handleScroll = () => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
    }

    settleTimerRef.current = setTimeout(selectClosestToCenter, 140);
  };

  const markUserScroll = () => {
    userDrivenScrollRef.current = true;
  };

  const handleTimeClick = (time) => {
    onChange?.(time);
    centerTime(time, "smooth");
  };

  useEffect(() => {
    if (!uniqueTimes.length) return;

    const autoCenterKey = `${uniqueTimesKey}|${restaurantTimezone}`;

    if (didAutoCenterKeyRef.current === autoCenterKey) return;

    const initialTime =
      value && uniqueTimes.includes(value)
        ? value
        : getClosestTimeToNow(uniqueTimes, restaurantTimezone);

    if (!initialTime) return;

    didAutoCenterKeyRef.current = autoCenterKey;

    onChange?.(initialTime);

    requestAnimationFrame(() => {
      centerTime(initialTime, "auto");
    });
  }, [uniqueTimesKey, restaurantTimezone]);

  useEffect(() => {
    if (!value || !uniqueTimes.includes(value)) return;

    if (ignoreNextValueCenterRef.current) {
      ignoreNextValueCenterRef.current = false;
      return;
    }

    centerTime(value, "smooth");
  }, [value, uniqueTimesKey]);

  useEffect(() => {
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  if (!uniqueTimes.length) return null;

  return (
    <ScrollWrap>
      <FadeLeft />

      <ScrollTrack
        ref={trackRef}
        onScroll={handleScroll}
        onWheel={markUserScroll}
        onTouchStart={markUserScroll}
        onPointerDown={markUserScroll}
        onKeyDown={markUserScroll}
      >
        {uniqueTimes.map((time) => (
          <TimePill
            key={time}
            ref={(element) => {
              if (element) {
                itemRefs.current[time] = element;
              }
            }}
            type="button"
            active={String(value) === String(time)}
            onClick={() => handleTimeClick(time)}
          >
            {time}
          </TimePill>
        ))}
      </ScrollTrack>

      <FadeRight />
    </ScrollWrap>
  );
}
